/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  RadioGroup,
  Tooltip,
  FormControlLabel,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectNotification } from 'modules/common/notifications/selectors';
import { selectOrganizationId } from 'modules/common/auth/selectors';
import { notificationActions } from 'modules/common/notifications/slice';
import ERROR_TYPES from 'modules/common/constants/error-types';
import TOAST_TYPES from 'modules/common/constants/toast-types';
import { hotelActions } from 'modules/hotels/slice';
import { addHotelValidation } from 'modules/hotels/validation/hotel-form-validation';
import { Alert, TextField } from '../style';
import '../index.scss';
import { THEMES } from '../../constants';
/**
 * Form to create hotel
 * @param {*} param0
 * @returns
 */
const AddHotelForm = ({ open, onClose }) => {
  const dispatch = useDispatch();
  //
  const notification = useSelector(selectNotification);
  const organizationId = useSelector(selectOrganizationId);
  //
  const modalAction = (action) => {
    if (notification?.isEnabled) dispatch(notificationActions.resetNotification());
    onClose(action);
  };
  //
  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      toast(notification?.message, { type: TOAST_TYPES.SUCCESS });
      modalAction(false);
    }
  }, [notification]);
  //
  const initialValues = {
    name: '',
    hotelCode: '',
    theme: '',
  };
  //
  const onSubmitHotel = async (values) => {
    const valueObj = {
      name: values?.name,
      hotelCode: values?.hotelCode,
      theme: values?.theme,
    };
    //
    dispatch(hotelActions.createHotel({ ...valueObj, organizationId }));
  };
  //
  return (
    <Dialog open={open} onClose={() => modalAction(false)}>
      <DialogTitle>Add Hotel</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={addHotelValidation}
          onSubmit={(values) => {
            onSubmitHotel(values);
          }}
        >
          {({ handleSubmit, handleBlur, handleChange, isSubmitting, touched, values, errors }) => (
            <form noValidate onSubmit={handleSubmit}>
              {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                <Alert mt={2} mb={3} severity={notification?.type}>
                  {notification?.message}
                </Alert>
              )}
              <TextField
                name="name"
                value={values.name}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
                my={2}
                label="Name"
                size="small"
              />
              <TextField
                name="hotelCode"
                value={values.hotelCode}
                error={Boolean(touched.hotelCode && errors.hotelCode)}
                helperText={touched.hotelCode && errors.hotelCode}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
                my={2}
                label="Hotel code"
                size="small"
              />
              <FormLabel>Theme</FormLabel>
              <RadioGroup
                row
                name="theme"
                value={values.theme}
                onChange={handleChange}
                sx={{ marginTop: 2, marginLeft: 2 }}
              >
                {THEMES.map((theme) => (
                  <Tooltip key={theme.name} title={theme.label}>
                    <FormControlLabel
                      value={theme.name}
                      control={
                        <input
                          type="radio"
                          name="theme"
                          value={theme.name}
                          checked={values.theme === theme.name}
                          onChange={handleChange}
                          style={{ display: 'none' }}
                        />
                      }
                      label={
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            backgroundColor: theme.color,
                            borderRadius: '50%',
                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                            cursor: 'pointer',
                            border:
                              values.theme === theme.name
                                ? '1px solid white'
                                : '2px solid transparent',
                            outline:
                              values.theme === theme.name ? `2px solid ${theme.color}` : 'none',
                            marginRight: 12,
                          }}
                        />
                      }
                    />
                  </Tooltip>
                ))}
              </RadioGroup>
              {errors.theme && (
                <FormHelperText sx={{ color: '#d32f2f' }}>{errors.theme}</FormHelperText>
              )}
              <DialogActions>
                <Button onClick={() => modalAction(false)} variant="contained" color="primary">
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="success" disabled={isSubmitting}>
                  Save
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
//
export default AddHotelForm;
